import { useEffect, useRef, useState } from "react";
import type { Playlist } from "@gooner/shared";
import { RESERVED_COLLECTION_IDS } from "@gooner/shared";
import { userApi } from "../../lib/api";
import { MASK } from "../../lib/replayMask";
import { BottomSheet } from "../../components/WhatsPlaying";
import { IconPlus } from "../ui/icons";
import { cx } from "../ui/primitives";

export type BulkDestination = { id: string; name: string } | { newName: string };

/**
 * Where a bulk Move / Add to… goes (issue #12): the user's own collections with their
 * counts, minus the one the page is on and the two reserved ones (Faps fills by the
 * crown, Your Uploads by uploading — the server refuses both), plus "+ New collection…".
 *
 * A popover over the action bar with a mouse, the shared BottomSheet on a phone — the
 * same thumb-reach sheet the Goontroller's pickers ride.
 */
export function DestinationPicker({
  mode,
  excludeId,
  uploadsSelected,
  sheet,
  onPick,
  onClose,
}: {
  mode: "move" | "add";
  excludeId: string;
  /** Some of the selection are the user's uploads: a public destination will leave
   *  those behind (uploads stay private), so the list says so beside it. */
  uploadsSelected: boolean;
  sheet: boolean;
  onPick: (to: BulkDestination) => void;
  onClose: () => void;
}) {
  const [playlists, setPlaylists] = useState<Playlist[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    userApi
      .listPlaylists()
      .then((r) => setPlaylists(r.playlists.filter((p) => !RESERVED_COLLECTION_IDS.has(p.id) && p.id !== excludeId)))
      .catch(() => setPlaylists([]));
  }, [excludeId]);

  // The popover closes on a press outside it or on Escape; the sheet brings its own.
  useEffect(() => {
    if (sheet) return;
    const onDoc = (e: MouseEvent) => {
      if (!(e.target instanceof Node && ref.current?.contains(e.target))) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [sheet, onClose]);

  const submitNew = () => {
    const name = newName.trim();
    if (name) onPick({ newName: name });
  };

  const body = (
    <>
      <div className={cx("overflow-y-auto", sheet ? "" : "max-h-64")}>
        {playlists === null ? (
          <p className="px-3 py-2 text-xs text-zinc-500">Loading…</p>
        ) : playlists.length === 0 ? (
          <p className="px-3 py-2 text-xs text-zinc-500">No other collections yet.</p>
        ) : (
          playlists.map((pl) => (
            <button
              key={pl.id}
              type="button"
              onClick={() => onPick({ id: pl.id, name: pl.name })}
              className={cx(
                "flex w-full items-center justify-between gap-3 px-3 text-left text-zinc-200 hover:bg-[var(--color-panel-2)]",
                sheet ? "py-3 text-sm" : "py-1.5 text-xs",
              )}
            >
              <span className="truncate" {...MASK}>
                {pl.name}
              </span>
              <span className="shrink-0 text-[11px] text-zinc-500">
                {uploadsSelected && pl.public ? "public · uploads stay · " : ""}
                {pl.clipCount}
              </span>
            </button>
          ))
        )}
      </div>
      <div className="border-t border-[var(--color-edge)] p-1.5">
        {creating ? (
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitNew();
              if (e.key === "Escape") {
                e.stopPropagation();
                setCreating(false);
              }
            }}
            placeholder="Collection name…"
            aria-label="New collection name"
            maxLength={80}
            className={cx(
              "w-full rounded bg-[var(--color-ink)] px-2 text-zinc-100 placeholder:text-zinc-600 focus:outline-none",
              sheet ? "py-2.5 text-sm" : "py-1 text-xs",
            )}
          />
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className={cx(
              "flex w-full items-center gap-1.5 rounded px-2 text-[var(--color-accent-soft)] hover:bg-[var(--color-panel-2)]",
              sheet ? "py-2.5 text-sm" : "py-1 text-xs",
            )}
          >
            <IconPlus width={13} height={13} />
            New collection…
          </button>
        )}
      </div>
    </>
  );

  const heading = mode === "move" ? "Move to collection" : "Add to collection";
  if (sheet) {
    return (
      <BottomSheet title={heading} onClose={onClose}>
        {body}
      </BottomSheet>
    );
  }
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={heading}
      className="absolute bottom-full right-0 mb-2 w-64 overflow-hidden rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] py-1 shadow-2xl"
    >
      <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-zinc-500">{heading}</p>
      {body}
    </div>
  );
}
